import { EntityContent } from 'src/content/entity-content.js';
import '../../library/fields/index.js';
import '../../library/editors/index.js';
import '../../library/editors/components/d-edit-classification.js';
import '../../library/lists/d-list-section-attachment.js';
import '../../library/lists/d-list-section.js';
import '../../library/components/d-view-attachments';
import { customElement } from 'lit/decorators.js';
import type { TemplateResult } from 'lit';
import { html } from 'lit';
import { isEmptyOrInvalidString } from 'src/utilities/text';
import { t } from 'src/i18n';
import type { UpdateSectionItem } from '../update-section-item';
import type { CompetenceViewEditItem } from './competence-view-edit-item';
import type { CompetenceView } from './competence-view';
import type { ListDefinition } from '../list-definition';
import type { SectionField } from '../../library/components/section-field';

// Per-record access control (klassifisering/begrenset tilgang) er foreløpig
// skjult for kompetanse – det gir lite mening for denne entiteten. Datamodellen
// og lagringen beholdes, så det kan skrus på igjen ved å sette denne til true.
const SHOW_ACCESS_CONTROL = false;

/**
 *
 * STATUS OK
 */
@customElement('d-competence-view')
export class DCompetenceView extends EntityContent<CompetenceView, CompetenceViewEditItem> {
  private get isCertificate(): boolean {
    return (this.editItem?.type ?? this.entityView.competenceType) === 'CERTIFICATE';
  }

  renderViewMode() {
    return html` ${this.renderFieldSection('competence', this.viewFields(), this.entityView)} `;
  }

  async initializeEditItem() {
    this.editItem = {
      employeeUuid: this.entityView.employeeUuid,
      type: this.entityView.competenceType,
      category: this.entityView.category,
      name: this.entityView.name,
      description: this.entityView.description,
      completedDate: this.entityView.completedDate,
      provider: this.entityView.provider,
      hours: this.entityView.hours === undefined ? '' : '' + this.entityView.hours,
      expiryDate: this.entityView.expiryDate,
      notifyDaysBefore: this.entityView.notifyDaysBefore === undefined ? '' : '' + this.entityView.notifyDaysBefore,
      notificationEnabled: this.entityView.notificationEnabled,
      certificateNumber: this.entityView.certificateNumber,
      classification: this.entityView.classification,
      accessControl: this.entityView.accessControl,
    };
  }

  doneDisabled(): boolean {
    return isEmptyOrInvalidString(this.editItem?.name);
  }

  renderEditItem(item: CompetenceViewEditItem): TemplateResult<1> {
    // Attachments (and their upload action) are shown in view mode only, via
    // lists(); the same pattern as documents/guidelines/assets.
    return html`
      ${SHOW_ACCESS_CONTROL ? this.renderEditClassification(item) : ''}
      ${this.renderFieldSection('competence', this.editFields(), item)}
    `;
  }

  renderViewAttachments() {
    return html`
      <d-view-attachments
        .writeAccess=${this.entityView.currentUserHasWriteAccess}
        field="competence_attachments"
        .items=${this.entityView.attachments}
        @upload-file=${this.onUploadFile}
      ></d-view-attachments>
    `;
  }

  protected viewFields(): SectionField[] {
    return [
      {
        field: 'classificationText',
        fieldForLabel: 'generalFields_access',
        type: 'view-text',
        class: 'fullWidth',
        condition: SHOW_ACCESS_CONTROL && this.entityView.classificationText !== '',
      },
      {
        field: 'category',
        type: 'view-text',
        class: 'fullWidth',
        condition: this.entityView.category !== '',
      },
      {
        field: 'statusText',
        type: 'view-text',
        class: 'minWidth300',
        condition: this.entityView.statusText !== '',
      },
      {
        field: 'completedDate',
        type: 'view-date',
        format: 'date-short',
        class: 'minWidth300',
      },
      {
        field: 'expiryDate',
        type: 'view-date',
        format: 'date-short',
        class: 'minWidth300',
        condition: this.entityView.competenceType === 'CERTIFICATE',
      },
      {
        field: 'provider',
        type: 'view-text',
        class: 'fullWidth',
        condition: this.entityView.provider !== '',
      },
      {
        field: 'certificateNumber',
        type: 'view-text',
        class: 'fullWidth',
        condition: this.entityView.competenceType === 'CERTIFICATE' && this.entityView.certificateNumber !== '',
      },
      {
        field: 'description',
        type: 'view-text',
        class: 'fullWidth preserve-newlines',
        condition: this.entityView.description !== '',
      },
    ];
  }

  protected lists(): ListDefinition[] {
    return [
      {
        field: 'competence_attachments',
        type: 'custom',
        icon: 'attachments',
        items: 'attachments',
        render: () => this.renderViewAttachments(),
      },
    ];
  }

  protected asUpdateSectionItem(): UpdateSectionItem | undefined {
    return undefined;
  }

  // Competence reuses the tutorials icon (there is no dedicated competence icon
  // asset, and competence is conceptually the same family as courses/tutorials).
  protected getIcon() {
    return 'tutorials';
  }

  private editFields(): SectionField[] {
    return [
      {
        field: 'type',
        type: 'edit-dropdown',
        options: [
          { value: 'COMPLETED_TRAINING', text: t('competence.types.completedTraining') },
          { value: 'CERTIFICATE', text: t('competence.types.certificate') },
        ],
        class: 'fullWidth',
      },
      {
        field: 'name',
        type: 'edit-text',
        placeholder: t('reports.fieldMustBeFilledOut'),
        autofocus: !this.entityView.isConfirmedEntity,
        markIfEmpty: true,
        class: 'fullWidth',
      },
      {
        field: 'category',
        type: 'edit-dropdown',
        options: this.entityView.categoryOptions,
        placeholder: t('competence.selectCategory'),
        class: 'fullWidth',
      },
      {
        field: 'provider',
        type: 'edit-text',
        class: 'fullWidth',
      },
      {
        field: 'completedDate',
        type: 'edit-date',
        class: 'minWidth300',
      },
      {
        field: 'hours',
        type: 'edit-text',
        class: 'minWidth300',
      },
      {
        field: 'expiryDate',
        type: 'edit-date',
        class: 'minWidth300',
        condition: this.isCertificate,
      },
      {
        field: 'certificateNumber',
        type: 'edit-text',
        class: 'fullWidth',
        condition: this.isCertificate,
      },
      {
        field: 'notifyDaysBefore',
        type: 'edit-text',
        class: 'minWidth300',
        condition: this.isCertificate,
      },
      {
        field: 'notificationEnabled',
        type: 'edit-checkbox',
        condition: this.isCertificate,
      },
      {
        field: 'description',
        type: 'edit-textarea',
        class: 'fullWidth',
      },
    ];
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-competence-view': DCompetenceView;
  }
}
